import { UserInfo } from "@shared/models";
import { create } from "zustand";

import { GroupMember } from "~/types/group";
import { UserHomeDataResponse } from "./api/userHome";
import { dispatchMemberChanged } from "./utils/cacheInvalidation";

export interface Conversation {
  conversationid: number;
}

export interface Member {
  id: number;
  name: string;
  birthdate?: string;
  age?: number; // Keep this for backward compatibility
  gender: "M" | "F" | "O";
  profileImageUrl?: string;
  email?: string;
}

// Helper functions for localStorage persistence
const STORAGE_KEY = "henophess_current_member";
const DEBUG_MODE_KEY = "henophess_debug_mode";
const USER_INFO_KEY = "henophess_user_info";
const CURRENT_GROUP_MEMBER_KEY = "henophess_current_group_member";
const USER_HOME_DATA_KEY = "henophess_user_home_data";

// Check if we're in a browser environment where localStorage is available
const isLocalStorageAvailable = () => {
  try {
    if (typeof window === "undefined") return false;
    if (!window.localStorage) return false;

    // Test localStorage functionality
    const testKey = "__test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.error("Failed to check localStorage availability:", e);
    return false;
  }
};

const saveCurrentMember = (member: Member | undefined) => {
  if (!isLocalStorageAvailable()) return;

  if (member) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(member));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
};

const loadCurrentMember = (): Member | undefined => {
  if (!isLocalStorageAvailable()) return undefined;

  const storedMember = localStorage.getItem(STORAGE_KEY);
  if (storedMember) {
    try {
      return JSON.parse(storedMember) as Member;
    } catch (error) {
      console.error("Failed to parse stored member:", error);
      return undefined;
    }
  }
  return undefined;
};

const saveCurrentGroupMember = (groupMember: GroupMember | undefined) => {
  if (!isLocalStorageAvailable()) return;

  if (groupMember) {
    localStorage.setItem(CURRENT_GROUP_MEMBER_KEY, JSON.stringify(groupMember));
  } else {
    localStorage.removeItem(CURRENT_GROUP_MEMBER_KEY);
  }
};

const loadCurrentGroupMember = (): GroupMember | undefined => {
  if (!isLocalStorageAvailable()) return undefined;

  const storedGroupMember = localStorage.getItem(CURRENT_GROUP_MEMBER_KEY);
  if (storedGroupMember) {
    try {
      return JSON.parse(storedGroupMember) as GroupMember;
    } catch (error) {
      console.error("Failed to parse stored group member:", error);
      return undefined;
    }
  }
  return undefined;
};

const loadDebugMode = (): boolean => {
  if (!isLocalStorageAvailable()) return false;

  const storedDebugMode = localStorage.getItem(DEBUG_MODE_KEY);
  if (storedDebugMode) {
    try {
      return JSON.parse(storedDebugMode) as boolean;
    } catch (error) {
      console.error("Failed to parse stored debug mode:", error);
      return false;
    }
  }
  return false;
};

const saveDebugMode = (debugMode: boolean) => {
  if (!isLocalStorageAvailable()) return;
  localStorage.setItem(DEBUG_MODE_KEY, JSON.stringify(debugMode));
};

const saveUserInfo = (userInfo: UserInfo | undefined) => {
  if (!isLocalStorageAvailable()) return;

  if (userInfo) {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  } else {
    localStorage.removeItem(USER_INFO_KEY);
  }
};

const loadUserInfo = (): UserInfo | undefined => {
  if (!isLocalStorageAvailable()) return undefined;

  const storedUserInfo = localStorage.getItem(USER_INFO_KEY);
  if (storedUserInfo) {
    try {
      return JSON.parse(storedUserInfo) as UserInfo;
    } catch (error) {
      console.error("Failed to parse stored userInfo:", error);
      return undefined;
    }
  }
  return undefined;
};

const saveUserHomeData = (userHomeData: UserHomeDataResponse | undefined) => {
  if (!isLocalStorageAvailable()) return;

  if (userHomeData) {
    localStorage.setItem(USER_HOME_DATA_KEY, JSON.stringify(userHomeData));
  } else {
    localStorage.removeItem(USER_HOME_DATA_KEY);
  }
};

const loadUserHomeData = (): UserHomeDataResponse | undefined => {
  if (!isLocalStorageAvailable()) return undefined;

  const storedUserHomeData = localStorage.getItem(USER_HOME_DATA_KEY);
  if (storedUserHomeData) {
    try {
      const data = JSON.parse(storedUserHomeData) as UserHomeDataResponse;

      // Convert string dates back to Date objects
      if (data.groupMembers) {
        data.groupMembers = data.groupMembers.map((gm) => ({
          ...gm,
          joinedAt: new Date(gm.joinedAt)
        }));
      }

      return data;
    } catch (error) {
      console.error("Failed to parse stored userHomeData:", error);
      return undefined;
    }
  }
  return undefined;
};

export interface AppState {
  userInfo?: UserInfo;
  isAuthorized: boolean;
  filter: string;
  currentMember?: Member;
  currentGroupMember?: GroupMember;
  editMode: boolean;
  debugMode: boolean;
  memberEntryMode: "default" | "addSelfInit" | "addMembersInit";
  userHomeData?: UserHomeDataResponse;

  setUserInfo: (userInfo: UserInfo | undefined) => void;
  setIsAuthorized: (isAuthorized: boolean) => void;
  setFilter: (filter: string) => void;
  setCurrentMember: (member: Member | undefined) => void;
  setCurrentGroupMember: (groupMember: GroupMember | undefined) => void;
  setEditMode: (editMode: boolean) => void;
  setDebugMode: (debugMode: boolean) => void;
  toggleDebugMode: () => void;
  setMemberEntryMode: (mode: "default" | "addSelfInit" | "addMembersInit") => void;
  setUserHomeData: (data: UserHomeDataResponse | undefined) => void;
}

export const useAppStore = create<AppState>()((set) => ({
  userInfo: loadUserInfo(),
  isAuthorized: true,
  filter: "",
  currentMember: loadCurrentMember(),
  currentGroupMember: loadCurrentGroupMember(),
  editMode: false,
  debugMode: loadDebugMode(),
  memberEntryMode: "default",
  userHomeData: loadUserHomeData(),

  setUserInfo: (userInfo) => {
    saveUserInfo(userInfo);
    set(() => ({ userInfo }));
  },
  setIsAuthorized: (isAuthorized) => set(() => ({ isAuthorized })),
  setFilter: (filter) => set(() => ({ filter })),
  setCurrentMember: (currentMember) => {
    saveCurrentMember(currentMember);

    // If we have currentGroupMember, dispatch event with its ID
    // This helps in optimizing cache invalidation for the specific member
    const state = useAppStore.getState();
    if (currentMember && state.currentGroupMember && state.currentGroupMember.member_id === currentMember.id) {
      // If we changed to the same member that's already in currentGroupMember, use that ID
      dispatchMemberChanged(state.currentGroupMember.id);
    } else {
      // Otherwise, dispatch event without ID for general updates
      dispatchMemberChanged();
    }

    set(() => ({ currentMember }));
  },
  setCurrentGroupMember: (currentGroupMember) => {
    saveCurrentGroupMember(currentGroupMember);

    // Dispatch member changed event with the group member ID
    if (currentGroupMember) {
      dispatchMemberChanged(currentGroupMember.id);
    }

    set(() => ({ currentGroupMember }));
  },
  setEditMode: (editMode) => set(() => ({ editMode })),
  setDebugMode: (debugMode) => {
    saveDebugMode(debugMode);
    set(() => ({ debugMode }));
  },
  toggleDebugMode: () =>
    set((state) => {
      const newDebugMode = !state.debugMode;
      saveDebugMode(newDebugMode);
      return { debugMode: newDebugMode };
    }),
  setMemberEntryMode: (memberEntryMode) => set(() => ({ memberEntryMode })),
  setUserHomeData: (userHomeData) => {
    saveUserHomeData(userHomeData);

    // Also update userInfo if available in the data
    if (userHomeData && userHomeData.user) {
      saveUserInfo(userHomeData.user);
    }

    // Update the store
    set((state) => {
      return {
        userHomeData,
        userInfo: userHomeData?.user || state.userInfo
      };
    });
  }
}));
